// Spor A T1.4 (DIS01/02/03/05-M): pure display rules for a received epikrise.
// Headings, priority meaning and labels are the protocol's own words.

import { MARKERING_OPTIONS, RECOMMENDATION_HEADING, type EpikriseKind, isEpikriseKind } from './epikrise';

/** DIS01-M 3.2, DIS02/03-M 3.2, DIS05-M 2.2: the heading per kind, exactly. */
export const RECEIVED_EPIKRISE_HEADING: Readonly<Record<EpikriseKind, string>> = {
  XDIS01: 'Udskrivningsepikrise',
  XDIS02: 'Ambulantepikrise',
  XDIS03: 'Skadestueepikrise',
  XDIS05: 'Billeddiagnostisk epikrise',
};

export function receivedEpikriseHeading(kind: string): string {
  return isEpikriseKind(kind) ? RECEIVED_EPIKRISE_HEADING[kind] : kind;
}

/** DIS02/03-M 2.3, DIS01-M 3.16: the rettet banner, in capitals. */
export const RETTET_BANNER = 'RETTET EPIKRISE – ERSTATTER TIDLIGERE EPIKRISE';

/** DIS01-M 3.17, DIS02/03-M 3.4/3.5, DIS05-M 2.5. */
export const ORIGINAL_RECEIVER_LABEL = 'Originalmodtager';
export const COPY_RECEIVER_LABEL = 'Kopimodtager';

export { RECOMMENDATION_HEADING };

export type PriorityChip = { label: 'Rød' | 'Gul' | 'Grøn'; meaning: string; className: string };

/**
 * DIS01-M 3.12/3.18–3.20: the priority with its colour and what it means
 * (MedCom's "Inspiration til implementering af markering"). Akut = Rød,
 * Subakut = Gul, Rutine = Grøn. Matching is case-insensitive (DIS01-M 2.2 sends
 * 'rutine'). null for no or an unknown priority.
 */
export function priorityChip(priority: string | null | undefined): PriorityChip | null {
  const value = (priority ?? '').trim().toLowerCase();
  const option = MARKERING_OPTIONS.find((o) => o.value !== 'none' && o.value.toLowerCase() === value);
  if (!option) return null;
  if (option.colour === 'red') return { label: 'Rød', meaning: option.text, className: 'bg-red-600 text-white' };
  if (option.colour === 'yellow') return { label: 'Gul', meaning: option.text, className: 'bg-yellow-300 text-gray-900' };
  return { label: 'Grøn', meaning: option.text, className: 'bg-green-600 text-white' };
}

/** DIS02/03-M 3.11/3.12: date and time as YYYYMMDDHHMM (time when sent). */
export function compactDateTime(dt: { date?: string | null; time?: string | null } | null | undefined): string | null {
  if (!dt) return null;
  const date = (dt.date ?? '').replace(/[^0-9]/g, '');
  if (date.length !== 8) return null;
  const time = (dt.time ?? '').replace(/[^0-9]/g, '').slice(0, 4);
  return `${date}${time.length === 4 ? time : ''}`;
}

/** Start/end labels per kind (DIS01-M 3.9/3.10, DIS02/03-M 3.11/3.12, DIS05-M 2.9/2.10). */
export function periodLabels(kind: string): { start: string; end: string } {
  switch (kind) {
    case 'XDIS01':
      return { start: 'Indlæggelse', end: 'Udskrivning' };
    case 'XDIS05':
      return { start: 'Undersøgelse påbegyndt', end: 'Undersøgelse afsluttet' };
    default:
      return { start: 'Første besøg', end: 'Sidste besøg' };
  }
}

// RelationCode values in MedCom's ValueLists.xsd: far, mor, barn, uspec_paaroerende.
const RELATION_LABELS: Readonly<Record<string, string>> = {
  far: 'Far',
  mor: 'Mor',
  barn: 'Barn',
  uspec_paaroerende: 'Pårørende',
};

export function relationLabel(code: string | null | undefined): string {
  const c = (code ?? '').trim();
  return RELATION_LABELS[c] ?? c;
}

/** DIS05-M 1.9 (DIS01-M 2.5): the answer link text. */
export function answersLabel(letterIdentifier: string | null | undefined, sentIso: string | null | undefined): string {
  const date = sentIso ? new Date(sentIso) : null;
  const shown = date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('da-DK', { timeZone: 'Europe/Copenhagen' })
    : '';
  return `Svar på henvisning ${letterIdentifier ?? ''} sendt ${shown}`.replace(/\s+/g, ' ').trim();
}

export type EpikriseTextSegment = { text: string; bold?: boolean; italic?: boolean; underline?: boolean; fixedFont?: boolean };
export type EpikriseTextLine = { segments: EpikriseTextSegment[]; align?: 'right' | 'center' };

export function lineText(line: EpikriseTextLine): string {
  return line.segments.map((s) => s.text).join('');
}
